import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';

const Highlights: React.FC = () => {
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    
    const videos = [
        new URL('../assets/videos/Video1.mp4', import.meta.url).href,
        new URL('../assets/videos/Video2.mp4', import.meta.url).href,
        new URL('../assets/videos/Video3.mp4', import.meta.url).href
    ];

    const handlePrevVideo = () => {
        setCurrentVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
    };

    const handleNextVideo = () => {
        setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
    };

    // Reset video and ensure play button works when changing videos
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.load();
        }
    }, [currentVideoIndex]);

    return (
        <section id="highlights" className="relative min-h-[40rem] py-24 px-8 bg-gradient-to-b from-[#0A0A0F] to-[#12121A] overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-l from-purple-500/10 to-blue-500/10 blur-[100px] rounded-full" />
            <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 blur-[100px] rounded-full" />

            <div className="max-w-7xl mx-auto relative">
                {/* Section Title */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h2 
                        className="text-4xl md:text-6xl font-bold"
                        style={{
                            background: 'linear-gradient(to right, #34D399, #14B8A6, #22C55E, #059669)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            color: 'transparent',
                            paddingBottom: '1rem'
                        }}
                    >
                        Featured Highlights
                    </h2>
                    <motion.div 
                        className="h-1 w-24 mx-auto mt-6 rounded-full bg-gradient-to-r from-emerald-400 via-teal-500 to-green-600"
                    />
                </motion.div>

                {/* Video Container */}
                <div className="relative">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative aspect-video w-full max-w-4xl mx-auto rounded-2xl overflow-hidden"
                    >
                        {/* Video Border Gradient */}
                        <div className="absolute inset-0 p-[1px] rounded-2xl bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-emerald-500/30" />
                        
                        {/* Video Element */}
                        <div className="relative w-full h-full">
                            <video
                                ref={videoRef}
                                key={currentVideoIndex}
                                className="w-full h-full rounded-2xl object-cover"
                                controls
                                playsInline
                                preload="auto"
                            >
                                <source src={videos[currentVideoIndex]} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            
                            {/* Small Navigation Buttons Overlay */}
                            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4 z-10">
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handlePrevVideo}
                                    className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors shadow-lg"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </motion.button>
                                
                                <div className="bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs">
                                    {currentVideoIndex + 1} / {videos.length}
                                </div>
                                
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleNextVideo}
                                    className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors shadow-lg"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Description */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="text-center text-gray-400 mt-8 max-w-2xl mx-auto"
                >
                    Experience the magic of our most memorable celebrations through this carefully curated highlight reel
                </motion.p>
            </div>
        </section>
    );
};

export default Highlights;
